/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type Category,
  type Product,
  type ProductResponse,
  type Property,
} from '@/types'
import { getPath, useParams } from '@/utils/next-util'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { useGetProductsByCategory } from './useProductHook'

export const sortTypes = [
  { label: 'Giá tăng dần', value: 'priceDesc' },
  { label: 'Giá giảm dần', value: 'priceAsc' },
  { label: 'Mới nhất', value: 'dateDesc' },
  { label: 'Bán chạy nhất', value: 'dateAsc' },
  { label: 'Tên: A – Z', value: 'alphaAsc' },
  { label: 'Tên: Z – A', value: 'alphaDesc' },
]
export const sortByTypes = [
  { label: 'Nên mua', type: 'custom', name: 'shouldBuy' },
  { label: 'Bán chạy', type: 'custom', name: 'bestSelling' },
  { label: 'Trả góp', type: 'custom', name: 'allowInstallment' },
  { label: 'Giá tăng dần', type: 'sortType', name: 'asc' },
  { label: 'Giá giảm dần', type: 'sortType', name: 'desc' },
]
export type searchParam = {
  propertyId: string
  propertyValueId: string
  value: string
}
export const useCategory = (
  category: Category,
  productData: ProductResponse
) => {
  const router = useRouter()
  const [routerChange, setRouterChange] = useState(false)
  const [totalProduct, setTotalProduct] = useState<number>(
    productData?.total || 0
  )
  const [totalPages, setTotalPages] = useState(productData?.pages)
  const [sortOptions] = useState(sortTypes)
  const [sortByOptions] = useState(sortByTypes)
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>(productData?.data)
  const [properties, setProperties] = useState<Property[]>(
    category?.properties.filter(e => e.filter)
  )
  const [highLightProperties, setHighLightProperties] = useState<Property[]>(
    category?.properties.filter(e => e.quickFilter)
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [currentParams, setCurrentParams] = useParams([
    {
      name: 'page',
      value: null,
    },
    {
      name: 'sortBy',
      value: null,
    },
    {
      name: 'sortType',
      value: null,
    },
    {
      name: 'bestSelling',
      value: null,
    },
    {
      name: 'shouldBuy',
      value: null,
    },
    {
      name: 'allowInstallment',
      value: null,
    },
    {
      name: 'property',
      value: null,
      filterKeys: properties?.map(g => ({
        name: getPath(g.slugs),
        value: null,
        data: g.values,
      })),
    },
  ])
  const { getProducts } = useGetProductsByCategory()

  const activeParams = currentParams
  const currentFilters = activeParams.filter(
    (f: any) =>
      ![
        'page',
        'sortBy',
        'sortType',
        'bestSelling',
        'shouldBuy',
        'allowInstallment',
      ].includes(f.name)
  )
  const activeSort = activeParams.find(
    (filter: any) => filter.name === 'sortType'
  ).value
  const activeSortCustom = activeParams
    .filter((filter: any) => filter.value === 'true')
    .map((item: { name: string }) => item.name) || ['']

  const activeFilters = currentFilters.reduce((acc: any, cur: any) => {
    if (cur.filterKeys) {
      return acc.concat(
        cur.filterKeys.map((e: any) => ({
          ...e,
          values: Array.isArray(e.value) ? e.value : e.value?.split(',') || [],
        }))
      )
    }
    const currentOptions = Array.isArray(cur.value)
      ? cur.value
      : cur.value?.split(',') || []

    return acc.push({
      name: cur.name,
      values: currentOptions,
    })
  }, [])

  const updateParams = useCallback(
    (params: any, changeRouter?: boolean) => {
      setCurrentPage(1)

      if (changeRouter) {
        setProducts([])
        setRouterChange(true)
        setLoading(true)
      }
      const newFilters = activeParams.map((filter: any) => {
        if (filter.filterKeys) {
          const filterKeys = filter.filterKeys.map((e: any) => {
            const matchedParam = params?.find((p: any) => p.name === e.name)
            return matchedParam ? { ...e, value: matchedParam?.value } : e
          })
          return {
            ...filter,
            filterKeys,
            value: filterKeys.map((e: any) => e.value),
          }
        }

        const matchedParam = params?.find((p: any) => p.name === filter.name)
        return matchedParam ? { ...filter, value: matchedParam?.value } : filter
      })
      setCurrentParams(newFilters)
    },
    [activeParams]
  )
  const loadMore = useCallback(() => {
    updateParams(
      [{ name: 'page', value: currentPage > 1 ? `${currentPage}` : null }],
      false
    )
  }, [productData, productData.limit, currentPage])

  useEffect(() => {
    if (!routerChange) {
      return
    }
    setLoading(true)
    getProducts(
      {
        limit: 30,
        categoryDefault: category._id,
        currentPage: currentPage,
        sortBy: 'catOrder',
        sortType: 'asc',
        catDisplay: true,
        ...router.query,
      },
      {
        onSuccess: res => {
          if (res.data.total !== totalProduct) {
            setProducts(res.data.data)
            setTotalProduct(res.data.total)
            setTotalPages(res.data.pages)
            setCurrentPage(1)
          } else {
            setProducts(products.concat(res.data.data))
            setTotalProduct(res.data.total)
            setTotalPages(res.data.pages)
          }
          setRouterChange(false)
          setLoading(false)
        },
      }
    )
  }, [router.query])

  useEffect(() => {
    if (category) {
      setHighLightProperties(category.properties.filter(e => e.quickFilter))
      setProperties(category.properties.filter(e => e.filter))
    }
  }, [category])

  useEffect(() => {
    if (productData) {
      setProducts(productData.data)
      setTotalProduct(productData.total)
      setTotalPages(productData.pages)
    }
  }, [productData])
  const applyFilterParams = () => {
    updateParams(currentParams, true)
  }

  const clearFilterParams = () => {
    if (category) {
      updateParams(
        category?.properties
          .filter(e => e.filter)
          .map(g => ({
            name: getPath(g.slugs),
            value: null,
          })),
        true
      )
    }
  }

  return {
    products,
    properties,
    updateParams,
    activeFilters,
    activeSort,
    currentPage,
    setProperties,
    loadMore,
    setCurrentPage,
    applyFilterParams,
    clearFilterParams,
    totalProduct,
    totalPages,
    setTotalPages,
    getProducts,
    setLoading,
    setProducts,
    highLightProperties,
    setHighLightProperties,
    loading,
    sortOptions,
    sortByOptions,
    activeSortCustom,
  }
}
